import { useState } from 'react';

import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import { Button } from '@nesso/ui/primitives/button';

import {
    type AddLicenseInput,
    type License,
    licensesOptions,
    useAddLicenseOptions,
    useRemoveLicenseOptions,
    useUpdateLicenseOptions,
} from '@/entities/practice-member';
import { useConfirmDialogStore } from '@/shared/stores/confirm-dialog-store';
import { SettingsRow } from '@/shared/ui/settings/settings-row';
import { SettingsSection } from '@/widgets/settings/settings-section';

import { ProfileLicenseForm } from './profile-licenses-section/profile-license-form';
import { ProfileLicenseRow } from './profile-licenses-section/profile-license-row';

type OpenForm = { kind: 'add' } | { kind: 'edit'; licenseId: string };

type Props = {
    isDisabled?: boolean;
    onEditingChange?: (isEditing: boolean) => void;
};

export function ProfileLicensesSection({ isDisabled, onEditingChange }: Props) {
    const { data: licenses } = useSuspenseQuery(licensesOptions());
    const [openForm, setOpenForm] = useState<OpenForm | null>(null);
    const addLicenseMutation = useMutation(useAddLicenseOptions());
    const updateLicenseMutation = useMutation(useUpdateLicenseOptions());
    const removeLicenseMutation = useMutation(useRemoveLicenseOptions());
    const openConfirmDialog = useConfirmDialogStore((state) => state.open);

    const openAddForm = () => {
        setOpenForm({ kind: 'add' });
        onEditingChange?.(true);
    };

    const openEditForm = (licenseId: string) => {
        setOpenForm({ kind: 'edit', licenseId });
        onEditingChange?.(true);
    };

    const closeForm = () => {
        setOpenForm(null);
        onEditingChange?.(false);
    };

    const handleAddSubmit = (data: AddLicenseInput) => {
        addLicenseMutation.mutate(data, { onSuccess: closeForm });
    };

    const handleEditSubmit = (licenseId: string, data: AddLicenseInput) => {
        updateLicenseMutation.mutate({ licenseId, ...data }, { onSuccess: closeForm });
    };

    const confirmDelete = (license: License) => {
        openConfirmDialog({
            title: `Delete ${license.state} license?`,
            cancelText: 'No, Cancel',
            confirmText: 'Yes, Delete',
            variant: 'destructive',
            onConfirm: () => removeLicenseMutation.mutate(license.id),
        });
    };

    const isAdding = openForm?.kind === 'add';
    // A form open anywhere in the section locks every row's Edit/Delete and the Add action,
    // matching the rest of the page's one-form-at-a-time rule.
    const isLocked = isDisabled || openForm !== null;

    return (
        <SettingsSection title="Licenses">
            {licenses.length === 0 && !isAdding && (
                <p className="pb-4 text-sm text-muted-foreground">No licenses on file yet.</p>
            )}

            {licenses.map((license) => {
                const isEditingThis =
                    openForm?.kind === 'edit' && openForm.licenseId === license.id;

                if (isEditingThis) {
                    return (
                        <SettingsRow
                            key={license.id}
                            label={`${license.state}, ${license.type}`}
                            action={{ label: 'Cancel', onClick: closeForm }}
                            className="gap-6"
                        >
                            <ProfileLicenseForm
                                defaultValues={{
                                    type: license.type as AddLicenseInput['type'],
                                    number: license.number,
                                    state: license.state as AddLicenseInput['state'],
                                    taxonomyCode: (license.taxonomyCode ??
                                        undefined) as AddLicenseInput['taxonomyCode'],
                                }}
                                onSubmit={(data) => handleEditSubmit(license.id, data)}
                                isSubmitting={updateLicenseMutation.isPending}
                            />
                        </SettingsRow>
                    );
                }

                return (
                    <ProfileLicenseRow
                        key={license.id}
                        license={license}
                        canDelete={licenses.length > 1}
                        isDisabled={isLocked}
                        onEdit={() => openEditForm(license.id)}
                        onDelete={() => confirmDelete(license)}
                    />
                );
            })}

            {isAdding ? (
                <SettingsRow
                    label="New license"
                    action={{ label: 'Cancel', onClick: closeForm }}
                    className="gap-6"
                >
                    <ProfileLicenseForm
                        onSubmit={handleAddSubmit}
                        isSubmitting={addLicenseMutation.isPending}
                    />
                </SettingsRow>
            ) : (
                <Button
                    variant="outline"
                    onClick={openAddForm}
                    disabled={isLocked}
                    aria-label="Add License"
                >
                    Add License
                </Button>
            )}
        </SettingsSection>
    );
}
